import { api } from '../api/client.js';

const CACHE_TTL_MS = 5 * 60 * 1000;
/** @type {Map<string, { payload: object, cacheAt: number }>} */
const cachedByTenant = new Map();
/** @type {Map<string, Promise<object>>} */
const inflightByTenant = new Map();

function getCacheTenantKey() {
    try {
        const user = JSON.parse(localStorage.getItem('alinea_user') || 'null');
        if (user?.tenantId != null) return String(user.tenantId);
        if (user?.tenant_id != null) return String(user.tenant_id);
    } catch {
        /* ignore */
    }
    return 'anon';
}

/** Fallback local — solo si la API falla o no responde. */
export const FALLBACK_HUMAN_CYCLE_CONFIG = {
    source: 'default',
    settings: {
        reviewIntervalDays: 90,
        title: 'Revisión de ciclo',
        lead: 'Las personas cambian.',
        body: 'Queremos saber si algo importante cambió en ti en este nuevo ciclo. No es una calificación: es un minuto para revisar cómo estás.',
        durationHint: '1 minuto · 10 preguntas',
        activeSeasonOverride: null,
        overrideUntil: null,
    },
    seasons: [
        {
            seasonKey: 'renovacion',
            label: 'Renovación',
            emoji: '🌱',
            tone: 'cultivar lo nuevo con calma',
            accent: '#6b9b6e',
            softBg: 'rgba(107, 155, 110, 0.12)',
            gradientSoft: 'linear-gradient(135deg, rgba(107,155,110,0.08), rgba(255,255,255,0))',
            startMonth: 1,
            endMonth: 3,
            monthRange: 'Ene – Mar',
            sortOrder: 1,
            isActive: true,
            questionFrames: {
                1: '¿Qué quieres cultivar al iniciar tu turno? 🌱',
            },
        },
        {
            seasonKey: 'expansion',
            label: 'Expansión',
            emoji: '☀️',
            tone: 'sostener energía con intención',
            accent: '#d4a017',
            softBg: 'rgba(212, 160, 23, 0.12)',
            gradientSoft: 'linear-gradient(135deg, rgba(212,160,23,0.08), rgba(255,255,255,0))',
            startMonth: 4,
            endMonth: 6,
            monthRange: 'Abr – Jun',
            sortOrder: 2,
            isActive: true,
            questionFrames: {
                1: '¿Qué capta tu atención con más energía al llegar? ☀️',
            },
        },
        {
            seasonKey: 'transicion',
            label: 'Transición',
            emoji: '🍂',
            tone: 'honrar lo que está cambiando',
            accent: '#c47c4a',
            softBg: 'rgba(196, 124, 74, 0.12)',
            gradientSoft: 'linear-gradient(135deg, rgba(196,124,74,0.08), rgba(255,255,255,0))',
            startMonth: 7,
            endMonth: 9,
            monthRange: 'Jul – Sep',
            sortOrder: 3,
            isActive: true,
            questionFrames: {
                1: 'Al llegar, ¿qué notas que está cambiando? 🍂',
            },
        },
        {
            seasonKey: 'regeneracion',
            label: 'Regeneración',
            emoji: '❄️',
            tone: 'cuidar ritmo y descanso',
            accent: '#5b8fa8',
            softBg: 'rgba(91, 143, 168, 0.12)',
            gradientSoft: 'linear-gradient(135deg, rgba(91,143,168,0.08), rgba(255,255,255,0))',
            startMonth: 10,
            endMonth: 12,
            monthRange: 'Oct – Dic',
            sortOrder: 4,
            isActive: true,
            questionFrames: {
                1: 'Al llegar, ¿qué necesitas cuidar primero? ❄️',
            },
        },
    ],
};

export function monthInSeasonRange(monthIndex, startMonth, endMonth) {
    const start = Number(startMonth) - 1;
    const end = Number(endMonth) - 1;
    if (start <= end) return monthIndex >= start && monthIndex <= end;
    return monthIndex >= start || monthIndex <= end;
}

export function resolveActiveSeason(config, date = new Date()) {
    const d = date instanceof Date ? date : new Date(date);
    const seasons = (config?.seasons || []).filter((s) => s.isActive !== false);
    const settings = config?.settings || {};

    if (settings.activeSeasonOverride) {
        const until = settings.overrideUntil ? new Date(settings.overrideUntil) : null;
        if (!until || until.getTime() > d.getTime()) {
            const forced = seasons.find((s) => s.seasonKey === settings.activeSeasonOverride);
            if (forced) return { ...forced, key: forced.seasonKey, forced: true };
        }
    }

    const month = d.getMonth();
    const match = seasons.find((s) => monthInSeasonRange(month, s.startMonth, s.endMonth));
    if (match) return { ...match, key: match.seasonKey, forced: false };

    const fallback = seasons[0] || FALLBACK_HUMAN_CYCLE_CONFIG.seasons[0];
    return { ...fallback, key: fallback.seasonKey, forced: false };
}

export function getCycleReviewIntroFromConfig(config, date = new Date()) {
    const season = resolveActiveSeason(config, date);
    const s = config?.settings || {};
    const defaults = FALLBACK_HUMAN_CYCLE_CONFIG.settings;
    return {
        title: s.title || defaults.title,
        lead: s.lead || defaults.lead,
        body: s.body || defaults.body,
        seasonLabel: `${season.emoji} ${season.label}`,
        seasonTone: season.tone,
        durationHint: s.durationHint || defaults.durationHint,
        seasonKey: season.key,
    };
}

export function getSeasonalQuestionFrameFromConfig(config, displayOrder, date = new Date()) {
    const season = resolveActiveSeason(config, date);
    const order = Number(displayOrder);
    return season.questionFrames?.[order] || season.questionFrames?.[String(order)] || null;
}

function normalizeApiPayload(data) {
    if (!data) return { ...FALLBACK_HUMAN_CYCLE_CONFIG };
    return {
        source: data.source || 'default',
        tenantId: data.tenantId ?? null,
        settings: data.settings || FALLBACK_HUMAN_CYCLE_CONFIG.settings,
        seasons: Array.isArray(data.seasons) ? data.seasons : FALLBACK_HUMAN_CYCLE_CONFIG.seasons,
        calendarCycle: data.calendarCycle,
        activeSeason: data.activeSeason,
        cycleReviewIntro: data.cycleReviewIntro,
    };
}

export async function loadHumanCycleConfig({ force = false, tenantKey } = {}) {
    const key = tenantKey != null ? String(tenantKey) : getCacheTenantKey();
    const now = Date.now();
    const cached = cachedByTenant.get(key);
    if (!force && cached && now - cached.cacheAt < CACHE_TTL_MS) {
        return cached.payload;
    }
    if (!force && inflightByTenant.has(key)) return inflightByTenant.get(key);

    const promise = (async () => {
        try {
            const res = await api.getHumanCycleConfig();
            const payload = normalizeApiPayload(res?.data);
            cachedByTenant.set(key, { payload, cacheAt: Date.now() });
            return payload;
        } catch (err) {
            console.warn('humanCycles fallback:', err?.message || err);
            return { ...FALLBACK_HUMAN_CYCLE_CONFIG, source: 'default_fallback' };
        } finally {
            inflightByTenant.delete(key);
        }
    })();

    inflightByTenant.set(key, promise);
    return promise;
}

export function clearHumanCycleCache(tenantKey) {
    if (tenantKey != null) {
        const key = String(tenantKey);
        cachedByTenant.delete(key);
        inflightByTenant.delete(key);
        return;
    }
    cachedByTenant.clear();
    inflightByTenant.clear();
}

/** Compatibilidad con módulos que esperan objeto estación simple. */
export function seasonToUiShape(season) {
    if (!season) return null;
    return {
        key: season.key || season.seasonKey,
        label: season.label,
        emoji: season.emoji,
        tone: season.tone,
        accent: season.accent,
        softBg: season.softBg,
        gradientSoft: season.gradientSoft,
    };
}
